<template>
  <v-layout>
    <v-dialog v-bind:value="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="1300">
      <template v-slot:activator="{ on }">
        <v-btn v-if="button_name" color="primary" class="mb-5" dark v-on="on" @click="$emit('updateDialog', true)">{{ button_name }}</v-btn>
      </template>

      <v-card>
        <v-form v-on:submit.prevent="createItem">
          <v-card-title>
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text class="pt-5 pb-2">

            <!-- Campos principales -->
            <v-row>
              <v-col cols="12" md="3">
                <v-combobox
                  v-model="item.emitter"
                  data-vv-as="emisor"
                  name="emitter"
                  item-text="name"
                  item-value="id"
                  :items="emitters"
                  label="Emisor*"
                  autocomplete="off"
                  v-validate="'required|combobox_required'"
                  :disabled="isViewModal() || item.id > 0"
                  return-object
                  @change="template_selected = null; loadAccounts()"
                ></v-combobox>
                <span class="red--text caption">{{ errors.first("emitter") }}</span>
              </v-col>

              <v-col cols="12" md="2">
                <DatePickerEsp
                  label="Fecha*"
                  name="date"
                  :date="item.date"
                  :isRequired="true"
                  :isViewModal="isViewModal()"
                  @setDate="
                    (date) => {
                      item.date = date;
                    }
                  "
                />
              </v-col>

              <v-col cols="12" md="3" v-if="showTemplates()">
                <v-combobox
                  v-model="template_selected"
                  item-text="description"
                  item-value="id"
                  :items="templates"
                  label="Plantilla"
                  autocomplete="off"
                  clearable
                  return-object
                  :disabled="!item.emitter"
                  @change="applyTemplate"
                ></v-combobox>
              </v-col>

              <v-col cols="12" :md="showTemplates() ? 4 : 7">
                <v-text-field
                  data-vv-as="descripción"
                  label="Descripción*"
                  name="description"
                  v-model="item.description"
                  v-validate="'required'"
                  autocomplete="off"
                  :disabled="isViewModal()"
                ></v-text-field>
                <span class="red--text caption">{{ errors.first("description") }}</span>
              </v-col>
            </v-row>

            <!-- Detalles Contables -->
            <div class="d-flex justify-space-between align-center mt-3 mb-3">
              <div class="d-flex align-center">
                <v-icon color="#1565C0" class="mr-2" size="20">format_list_bulleted</v-icon>
                <span class="subtitle-1 font-weight-bold grey--text text--darken-3">Detalles Contables</span>
              </div>
              <v-btn v-if="!isViewModal()" color="primary" small @click="addLinea">
                <v-icon small left>add</v-icon>
                Agregar Item
              </v-btn>
            </div>

            <!-- Tabla de líneas -->
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="detail-th">#</th>
                    <th class="detail-th">Cuenta</th>
                    <th class="detail-th">Concepto</th>
                    <th class="detail-th">Centro de Costo</th>
                    <th class="detail-th text-center">Debe</th>
                    <th class="detail-th text-center">Haber</th>
                    <th class="detail-th" v-if="!isViewModal()"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(linea, index) in item.lineas" :key="index">
                    <td class="text-center grey--text text--darken-1" style="min-width:40px; font-size:13px; font-weight:500">
                      {{ String(index + 1).padStart(2, '0') }}
                    </td>
                    <td style="min-width:190px">
                      <v-combobox
                        v-model="linea.account"
                        item-text="label"
                        item-value="id"
                        :items="accountsImputables"
                        autocomplete="off"
                        hide-details
                        :disabled="isViewModal()"
                        return-object
                        class="py-1"
                      ></v-combobox>
                    </td>
                    <td style="min-width:160px">
                      <v-combobox
                        v-if="esResultado(linea)"
                        v-model="linea.concepto"
                        item-text="name"
                        item-value="id"
                        :items="conceptos"
                        autocomplete="off"
                        hide-details
                        clearable
                        :disabled="isViewModal()"
                        return-object
                        class="py-1"
                      ></v-combobox>
                      <span v-else class="grey--text caption">—</span>
                    </td>
                    <td style="min-width:160px">
                      <v-combobox
                        v-if="esResultado(linea)"
                        v-model="linea.cost_center"
                        item-text="name"
                        item-value="id"
                        :items="cost_centers"
                        autocomplete="off"
                        hide-details
                        clearable
                        :disabled="isViewModal()"
                        return-object
                        class="py-1"
                      ></v-combobox>
                      <span v-else class="grey--text caption">—</span>
                    </td>
                    <td style="min-width:120px" :class="isViewModal() ? 'text-right' : ''">
                      <span v-if="isViewModal()">{{ amountOf(linea.debe) > 0 ? formatUtils.formatMoney(amountOf(linea.debe)) : "—" }}</span>
                      <v-text-field
                        v-else
                        v-model="linea.debe"
                        hide-details
                        class="py-1 input-right"
                        :disabled="amountOf(linea.haber) > 0"
                        @blur="normalizeAmount(linea, 'debe')"
                      ></v-text-field>
                      <span v-if="isInvalidAmount(linea.debe)" class="red--text caption">Importe invalido.</span>
                    </td>
                    <td style="min-width:120px" :class="isViewModal() ? 'text-right' : ''">
                      <span v-if="isViewModal()">{{ amountOf(linea.haber) > 0 ? formatUtils.formatMoney(amountOf(linea.haber)) : "—" }}</span>
                      <v-text-field
                        v-else
                        v-model="linea.haber"
                        hide-details
                        class="py-1 input-right"
                        :disabled="amountOf(linea.debe) > 0"
                        @blur="normalizeAmount(linea, 'haber')"
                      ></v-text-field>
                      <span v-if="isInvalidAmount(linea.haber)" class="red--text caption">Importe invalido.</span>
                    </td>
                    <td v-if="!isViewModal()" style="width:40px">
                      <v-icon color="red lighten-1" small @click="removeLinea(index)">delete</v-icon>
                    </td>
                  </tr>
                  <tr v-if="item.lineas.length === 0">
                    <td colspan="7" class="text-center grey--text py-4">Sin líneas — hacé click en "Agregar Item"</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Panel de totales -->
            <div class="d-flex justify-end mt-4 mb-2">
              <v-card outlined class="pa-4" style="min-width: 300px">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="body-2 grey--text text--darken-1">Total Debe</span>
                  <span class="body-2 font-weight-medium">{{ formatUtils.formatMoney(totalDebe) }}</span>
                </div>
                <div class="d-flex justify-space-between align-center mb-3">
                  <span class="body-2 grey--text text--darken-1">Total Haber</span>
                  <span class="body-2 font-weight-medium">{{ formatUtils.formatMoney(totalHaber) }}</span>
                </div>
                <v-divider class="mb-3"></v-divider>
                <div
                  class="d-flex justify-space-between align-center rounded pa-3"
                  :style="isBalanced ? 'background:#E8F5E9' : 'background:#FFF3E0'"
                >
                  <div>
                    <div class="caption font-weight-bold text-uppercase grey--text text--darken-2">Total Asiento</div>
                    <div class="caption font-weight-medium" :class="isBalanced ? 'green--text text--darken-1' : 'orange--text text--darken-3'">
                      {{ isBalanced ? 'BALANCEADO' : 'SIN BALANCEAR' }}
                    </div>
                  </div>
                  <span class="text-h6 font-weight-bold" :class="isBalanced ? 'green--text text--darken-1' : 'red--text'">
                    {{ formatUtils.formatMoney(totalDebe) }}
                  </span>
                </div>
              </v-card>
            </div>

          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn color="success" text type="submit" v-if="!isViewModal()" :loading="loading" :disabled="diferencia !== 0 || totalDebe === 0">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ModalGenericMixin from "@/mixins/ModalGenericMixin.vue";
import ConstantSections from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { formatUtils } from "@/libs/utils/formatUtils";
import DatePickerEsp from "@/components/DatePickerEsp.vue";

export default {
  mixins: [ModalGenericMixin],
  components: { DatePickerEsp },
  props: {
    service: Object,
    modal_title: String,
    dialog: Boolean,
    item: Object,
    button_name: String,
    formBtn: String,
    emitters: Array,
  },
  data() {
    return {
      formatUtils: formatUtils,
      accounts: [],
      cost_centers: [],
      conceptos: [],
      templates: [],
      template_selected: null,
    };
  },
  computed: {
    accountsImputables() {
      return this.accounts.filter((a) => a.imputable);
    },
    totalDebe() {
      return Math.round(this.item.lineas.reduce((sum, l) => sum + this.amountOf(l.debe), 0) * 100) / 100;
    },
    totalHaber() {
      return Math.round(this.item.lineas.reduce((sum, l) => sum + this.amountOf(l.haber), 0) * 100) / 100;
    },
    diferencia() {
      return Math.round((this.totalDebe - this.totalHaber) * 100) / 100;
    },
    isBalanced() {
      return this.totalDebe > 0 && this.diferencia === 0;
    },
  },
  watch: {
    item() {
      this.$emit("updateItem", this.item);
    },
    dialog(val) {
      this.errors.clear();
      this.$validator.reset();
      if (val) {
        this.template_selected = null;
        this.loadAccounts();
        if (!this.item.lineas || this.item.lineas.length === 0) {
          this.item.lineas = [];
          this.addLinea();
          this.addLinea();
        }
      }
    },
  },
  mounted() {
    generalService.filter({ enabled: true }, ConstantSections.SECTION_COSTS_CENTERS).then((response) => {
      this.cost_centers = (response.result || []).map((cc) => ({ id: cc.id, name: cc.name }));
    });
    generalService.filter({ enabled: true }, ConstantSections.SECTION_CONCEPTOS).then((response) => {
      this.conceptos = (response.result || []).map((k) => ({ id: k.id, name: k.name }));
    });
  },
  methods: {
    isViewModal() {
      return this.formBtn == "";
    },
    amountOf(value) {
      return formatUtils.parseAmount(value) || 0;
    },
    //Reescribe el campo con el importe ya normalizado (soporta lo pegado desde Excel: "10.822.496,52")
    normalizeAmount(linea, field) {
      linea[field] = formatUtils.normalizeAmountInput(linea[field]);
    },
    isInvalidAmount(value) {
      if (value === null || value === undefined || value === "") return false;
      //Ignora los estados intermedios mientras se tipea ("-", ",", "-,")
      if (/^[-.,\s]*$/.test(String(value))) return false;
      return formatUtils.parseAmount(value) === null;
    },
    esResultado(linea) {
      return linea.account != null && linea.account.is_resultado === true;
    },
    addLinea() {
      this.item.lineas.push({ id: 0, account: null, debe: 0, haber: 0, cost_center: null, concepto: null, description: null });
    },
    removeLinea(index) {
      this.item.lineas.splice(index, 1);
    },
    loadAccounts() {
      if (!this.item.emitter || !this.item.emitter.id) {
        this.accounts = [];
        this.templates = [];
        return;
      }
      generalService.filter({ emitter: this.item.emitter.id }, ConstantSections.SECTION_ACCOUNTS).then((response) => {
        this.accounts = response.result || [];
      });
      if (this.showTemplates()) {
        generalService.filter({ emitter: this.item.emitter.id, enabled: true }, ConstantSections.SECTION_ASIENTO_TEMPLATES).then((response) => {
          this.templates = response.result || [];
        });
      }
    },
    showTemplates() {
      return !this.isViewModal() && !(this.item.id > 0);
    },
    //Reemplaza las líneas por las de la plantilla, con importes en 0
    applyTemplate(template) {
      if (!template || !template.id) return;
      this.item.lineas = template.items.map((i) => ({
        id: 0,
        account: i.account,
        debe: 0,
        haber: 0,
        cost_center: i.cost_center,
        concepto: i.concepto,
        description: null,
      }));
      if (!this.item.description) this.item.description = template.description;
    },
    createItem(e) {
      if (this.loading) return;
      this.$validator.validate().then((valid) => {
        if (valid) {
          e.preventDefault();

          if (!this.item.date) {
            this.$toast.error("Debe ingresar la fecha.", "Error");
            return;
          }

          const lineasValidas = this.item.lineas.filter((l) => l.account != null);
          if (lineasValidas.length < 2) {
            this.$toast.error("El asiento debe tener al menos 2 líneas con cuenta.", "Error");
            return;
          }

          for (let i = 0; i < lineasValidas.length; i++) {
            const l = lineasValidas[i];
            if (this.isInvalidAmount(l.debe) || this.isInvalidAmount(l.haber)) {
              this.$toast.error(`Línea ${i + 1}: el importe debe ser un número válido.`, "Error");
              return;
            }
          }

          this.loading = true;
          this.service.setSection(ConstantSections.SECTION_ASIENTOS);

          const payload = {
            id: this.item.id || null,
            date: this.item.date,
            description: this.item.description,
            emitter: this.item.emitter ? { id: this.item.emitter.id } : null,
            lineas_arr: lineasValidas.map((l) => ({
              id: l.id || 0,
              account: { id: l.account.id },
              debe: this.amountOf(l.debe),
              haber: this.amountOf(l.haber),
              cost_center: l.cost_center ? { id: l.cost_center.id } : null,
              concepto: l.concepto ? { id: l.concepto.id } : null,
              description: l.description || null,
            })),
          };

          const call = this.item.id > 0 ? this.service.update(payload) : this.service.create(payload);
          call
            .then((response) => {
              if (response.status == true) {
                const verb = this.item.id > 0 ? "modifico" : "creo";
                this.$toast.success("El asiento se " + verb + " correctamente.", "OK");
                this.$emit("updateDialog", false);
                this.$emit("loadList");
              } else {
                this.$toast.error(response.msg || response.message, "Error");
              }
            })
            .catch(() => {
              this.$toast.error("Ocurrió un error al guardar.", "Error");
            })
            .finally(() => (this.loading = false));
        }
      });
    },
  },
};
</script>

<style scoped>
.detail-th {
  background-color: #F5F5F5 !important;
  color: #616161 !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.input-right input {
  text-align: right;
}
</style>
